import { NgModule } from '@angular/core';
import { mapToCanActivate, RouterModule, Routes } from '@angular/router';
import { ExistingAccountGuard } from '@kp/auth/guards/existing-account.guard';
import { ProductGuard } from '@kp/auth/guards/product.guard';
import { SetPasswordGuard } from '@kp/auth/guards/set-password.guard';
import { TagGuard } from '@kp/core/tags/tag.guard';
import { EligibilityHeaderComponent } from '@kp/eligibility/eligibility-header/eligibility-header.component';
import { EligibilityVerificationTypeComponent } from '@kp/eligibility/screen-eligibility-selection/components/eligibility-verification-type/eligibility-verification-type.component';
import { ScreenEligibilitySelectionComponent } from '@kp/eligibility/screen-eligibility-selection/screen-eligibility-selection.component';
import { EligibilityErrorComponent } from '@kp/eligibility/screen-eligibility/eligibility/components/eligibility-error/eligibility-error.component';
import { EligibilityPersonalDetailsComponent } from '@kp/eligibility/screen-eligibility/eligibility/components/eligibility-personal-details/eligibility-personal-details.component';
import { EligibilitySingleValueComponent } from '@kp/eligibility/screen-eligibility/eligibility/components/eligibility-single-value/eligibility-single-value.component';
import { EligibilityComponent } from '@kp/eligibility/screen-eligibility/eligibility/eligibility.component';
import { ScreenEligibilityComponent } from '@kp/eligibility/screen-eligibility/screen-eligibility.component';
import { CheckSubscriptionStatusGuard } from '@kp/onboarding/guards/check-subscription-status.guard';
import { EligibilityTypeRoutingGuard } from '@kp/onboarding/guards/eligibility-type-routing-guard.service';
import { EligibilityVerificationGuard } from '@kp/onboarding/guards/eligibility-verification.guard';
import { GroupCodeIsValidGuard } from '@kp/onboarding/guards/group-code-is-valid.guard';
import { WorkbookOrderedGuard } from '@kp/onboarding/guards/workbook-ordered.guard';
import { WorkbookGuard } from '@kp/onboarding/guards/workbook.guard';
import { ScreenEmailSignupBaseComponent } from '@kp/onboarding/screen-email-signup-base/screen-email-signup-base.component';
import { ScreenFullPrivacyComponent } from '@kp/onboarding/screen-full-privacy/screen-full-privacy.component';
import { LandingFooterComponent } from '@kp/onboarding/screen-landing-dynamic/landing-footer/landing-footer.component';
import { LandingHeaderComponent } from '@kp/onboarding/screen-landing-dynamic/landing-header/landing-header.component';
import { LandingSectionComponent } from '@kp/onboarding/screen-landing-dynamic/landing-section/landing-section.component';
import { ScreenLandingDynamicComponent } from '@kp/onboarding/screen-landing-dynamic/screen-landing-dynamic.component';
import { ScreenMobileAppInterstitialComponent } from '@kp/onboarding/screen-mobile-interstitial/screen-mobile-app-interstitial.component';
import { ScreenOnboardingErrorComponent } from '@kp/onboarding/screen-onboarding-error/screen-onboarding-error.component';
import { ScreenPreSignupComponent } from '@kp/onboarding/screen-pre-signup/screen-pre-signup.component';
import { ScreenPrivacyPledgeComponent } from '@kp/onboarding/screen-privacy-pledge/screen-privacy-pledge.component';
import { ScreenSignupPasswordComponent } from '@kp/onboarding/screen-signup-password/screen-signup-password.component';
import { ScreenSignupComponent } from '@kp/onboarding/screen-signup/screen-signup.component';
import { ScreenVerificationEmailComponent } from '@kp/onboarding/screen-verification-email/screen-verification-email.component';
import { ScreenVerificationNoCodeComponent } from '@kp/onboarding/screen-verification-no-code/screen-verification-no-code.component';
import { ModalWorkbookComponent } from '@kp/workbook/modal-workbook/modal-workbook.component';
import { ScreenWorkbookConfirmedComponent } from '@kp/workbook/screen-workbook-confirmed/screen-workbook-confirmed.component';
import { ScreenWorkbookEditComponent } from '@kp/workbook/screen-workbook-edit/screen-workbook-edit.component';
import { ScreenWorkbookPromoComponent } from '@kp/workbook/screen-workbook-promo/screen-workbook-promo.component';
import { ScreenWorkbookReviewComponent } from '@kp/workbook/screen-workbook-review/screen-workbook-review.component';
import { TagKey } from '@ktypes/models';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    component: ScreenLandingDynamicComponent,
    canActivate: mapToCanActivate([GroupCodeIsValidGuard]),
  },
  {
    path: 'eligibility',
    component: ScreenEligibilitySelectionComponent,
    canActivate: mapToCanActivate([GroupCodeIsValidGuard, EligibilityTypeRoutingGuard]),
  },
  {
    path: 'mobile-app/interstitial',
    component: ScreenMobileAppInterstitialComponent,
    canActivate: mapToCanActivate([
      CheckSubscriptionStatusGuard,
      GroupCodeIsValidGuard,
      ProductGuard,
      ExistingAccountGuard,
      TagGuard,
      WorkbookGuard,
    ]),
    data: {
      productGuardRedirectUrl: '/welcome/notice',
      excludedTags: [{ tag: TagKey.no_QRcode, redirectUrl: '/welcome/notice' }],
    },
  },
  {
    // mobile devices should ignore the no_QRcode tag as it relates to the mobile app interstitial
    path: 'mobile-app/interstitial/mobile',
    component: ScreenMobileAppInterstitialComponent,
    canActivate: mapToCanActivate([
      CheckSubscriptionStatusGuard,
      GroupCodeIsValidGuard,
      ProductGuard,
      ExistingAccountGuard,
      WorkbookGuard,
    ]),
    data: {
      productGuardRedirectUrl: '/welcome/notice',
    },
  },
  {
    path: 'eligibility/:eligibilityType',
    component: ScreenEligibilityComponent,
    canActivate: mapToCanActivate([GroupCodeIsValidGuard]),
  },
  {
    path: 'workbook',
    component: ScreenWorkbookPromoComponent,
    canActivate: mapToCanActivate([GroupCodeIsValidGuard, EligibilityVerificationGuard, WorkbookOrderedGuard]),
  },
  {
    path: 'workbook/edit',
    component: ScreenWorkbookEditComponent,
    canActivate: mapToCanActivate([GroupCodeIsValidGuard, EligibilityVerificationGuard, WorkbookOrderedGuard]),
  },
  {
    path: 'workbook/review',
    component: ScreenWorkbookReviewComponent,
    canActivate: mapToCanActivate([GroupCodeIsValidGuard, EligibilityVerificationGuard, WorkbookOrderedGuard]),
  },
  {
    path: 'workbook/confirmed',
    component: ScreenWorkbookConfirmedComponent,
    canActivate: mapToCanActivate([GroupCodeIsValidGuard, EligibilityVerificationGuard]),
  },
  {
    path: 'notice',
    component: ScreenPrivacyPledgeComponent,
    canActivate: mapToCanActivate([
      CheckSubscriptionStatusGuard,
      GroupCodeIsValidGuard,
      ExistingAccountGuard,
      EligibilityVerificationGuard,
    ]),
  },
  {
    path: 'ready',
    component: ScreenPreSignupComponent,
    canActivate: mapToCanActivate([CheckSubscriptionStatusGuard, GroupCodeIsValidGuard]),
  },
  {
    path: 'privacy',
    component: ScreenFullPrivacyComponent,
    canActivate: mapToCanActivate([CheckSubscriptionStatusGuard, GroupCodeIsValidGuard]),
  },
  {
    path: 'signup',
    component: ScreenSignupComponent,
    canActivate: mapToCanActivate([SetPasswordGuard]),
  },
  {
    path: 'signup/verification/email',
    component: ScreenVerificationEmailComponent,
    canActivate: mapToCanActivate([SetPasswordGuard]),
  },
  {
    path: 'signup/verification/no-code',
    component: ScreenVerificationNoCodeComponent,
  },
  {
    path: 'signup/password',
    component: ScreenSignupPasswordComponent,
  },
  { path: 'error', component: ScreenOnboardingErrorComponent },
  { path: '**', pathMatch: 'full', redirectTo: 'error' },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class OnboardingRoutingModule {
  static components = [
    EligibilityComponent,
    EligibilitySingleValueComponent,
    EligibilityPersonalDetailsComponent,
    EligibilityErrorComponent,
    EligibilityHeaderComponent,
    EligibilityVerificationTypeComponent,
    LandingFooterComponent,
    LandingHeaderComponent,
    LandingSectionComponent,
    ModalWorkbookComponent,
    ScreenEligibilityComponent,
    ScreenEligibilitySelectionComponent,
    ScreenEmailSignupBaseComponent,
    ScreenFullPrivacyComponent,
    ScreenLandingDynamicComponent,
    ScreenMobileAppInterstitialComponent,
    ScreenOnboardingErrorComponent,
    ScreenPreSignupComponent,
    ScreenPrivacyPledgeComponent,
    ScreenSignupComponent,
    ScreenSignupPasswordComponent,
    ScreenVerificationEmailComponent,
    ScreenVerificationNoCodeComponent,
    ScreenWorkbookEditComponent,
    ScreenWorkbookReviewComponent,
    ScreenWorkbookConfirmedComponent,
    ScreenWorkbookPromoComponent,
  ];
}
